import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link, useSearchParams } from 'react-router-dom';
import BookNavigationTabs from '../components/BookNavigationTabs';
import { useSyncLanguageUrl } from '../hooks/useSyncLanguageUrl';

function CreatePagesFromBook() {
  const [searchParams, setSearchParams] = useSearchParams(); // Get search parameters
  const bookId = searchParams.get('bookId'); // Get bookId from search parameters
  const [books, setBooks] = useState([]);
  const [selectedBookId, setSelectedBookId] = useState('');
  const [pdfFile, setPdfFile] = useState(null);
  const [singlePage, setSinglePage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [whitePageLoading, setWhitePageLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [pageLanguage, setPageLanguage] = useState('es_de');
  const [latestPageNumber, setLatestPageNumber] = useState(0);
  const [pageCount, setPageCount] = useState(0);

  // Sync plang parameter with URL
  useSyncLanguageUrl(pageLanguage);

  useEffect(() => {
    const plang = searchParams.get('plang');
    if (plang) {
      setPageLanguage(plang);
    }
  }, [searchParams]);

  useEffect(() => {
    async function fetchBooks() {
      try {
        const response = await axios.get(`/api/list_books`);
        console.log(response.data);
        setBooks(response.data);
        console.log(bookId);

        // Pre-select book if bookId is in the URL
        if (bookId) {
          const bookExists = response.data.some((book) => book._id === bookId);
          if (bookExists) {
            setSelectedBookId(bookId);
          } else {
            setMessage('Book ID from URL not found.');
          }
        }
      } catch (error) {
        setMessage('Error fetching books.');
      }
    }

    fetchBooks();
  }, [bookId]);

  useEffect(() => {
    if (!selectedBookId) {
      setLatestPageNumber(0);
      setPageCount(0);
      return;
    }

    async function fetchPageSummary() {
      try {
        const response = await axios.get(`/api/list_pages/${selectedBookId}`);
        const pages = response.data.pages || [];
        const lastPageNumber = pages.reduce(
          (maxPageNumber, page) => Math.max(maxPageNumber, page.page_number || 0),
          0,
        );
        setLatestPageNumber(lastPageNumber);
        setPageCount(pages.length);
      } catch (error) {
        setMessage('Error fetching pages for this book.');
      }
    }

    fetchPageSummary();
  }, [selectedBookId]);

  const handlePdfChange = (event) => {
    setPdfFile(event.target.files[0]);
    setSinglePage(null);
  };

  const handleSinglePageChange = (event) => {
    setSinglePage(event.target.files[0]);
    setPdfFile(null);
  };

  const handleBookSelection = (nextBookId) => {
    setSelectedBookId(nextBookId);

    const nextParams = new URLSearchParams(searchParams);
    if (nextBookId) {
      nextParams.set('bookId', nextBookId);
    } else {
      nextParams.delete('bookId');
    }
    if (pageLanguage) {
      nextParams.set('plang', pageLanguage);
    }

    setSearchParams(nextParams, { replace: false });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    console.log('selectedBookId on submit:', selectedBookId);

    if (!selectedBookId) {
      setMessage('Please select a book.');
      return;
    }

    if (!pdfFile && !singlePage) {
      setMessage('Please select a PDF file or a single page.');
      return;
    }

    setLoading(true);
    setMessage('');

    try {
      const formData = new FormData();
      if (pdfFile) {
        formData.append('pdf', pdfFile);
      } else if (singlePage) {
        formData.append('page', singlePage);
      }
      formData.append('bookId', selectedBookId);

      const response = await axios.post(
        `/api/books/${selectedBookId}/add_pages`,
        formData,
      );
      setMessage(response.data.message);

      setPdfFile(null);
      setSinglePage(null);

      const pagesResponse = await axios.get(`/api/list_pages/${selectedBookId}`);
      const pages = pagesResponse.data.pages || [];
      const lastPageNumber = pages.reduce(
        (maxPageNumber, page) => Math.max(maxPageNumber, page.page_number || 0),
        0,
      );
      setLatestPageNumber(lastPageNumber);
      setPageCount(pages.length);
    } catch (error) {
      setMessage('Error adding pages to the book. Try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleAddWhitePage = async () => {
    if (!selectedBookId) {
      setMessage('Please select a book.');
      return;
    }

    setWhitePageLoading(true);
    setMessage('');

    try {
      await axios.post(`/api/books/${selectedBookId}/insert_blank_page`, {
        insert_after_page_number: latestPageNumber,
      });

      const pagesResponse = await axios.get(`/api/list_pages/${selectedBookId}`);
      const pages = pagesResponse.data.pages || [];
      const lastPageNumber = pages.reduce(
        (maxPageNumber, page) => Math.max(maxPageNumber, page.page_number || 0),
        0,
      );
      setLatestPageNumber(lastPageNumber);
      setPageCount(pages.length);
      setMessage('White page added at the end.');
    } catch (error) {
      setMessage('Error adding white page. Try again.');
    } finally {
      setWhitePageLoading(false);
    }
  };

  const selectedBook = books.find((book) => book._id === selectedBookId);

  return (
    <div className="max-w-[1400px] mx-auto p-6 bg-white shadow-lg rounded-lg mt-10">
      {selectedBookId && (
        <BookNavigationTabs
          bookId={selectedBookId}
          currentLanguage={pageLanguage}
          headerTitle={selectedBook?.name || 'Add Pages'}
          headerLinkTo={`/books/${selectedBookId}/edit`}
          showBackButton
          showFlags={false}
          showBookSwitcher
        />
      )}

      {!selectedBookId && (
        <h2 className="text-2xl font-bold text-center mb-4">Add Pages to Book</h2>
      )}

      {message && <p className="text-center text-red-500 mb-4">{message}</p>}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-gray-700 font-medium">
            Select Book:
          </label>
          <select
            value={selectedBookId}
            onChange={(e) => handleBookSelection(e.target.value)}
            className="w-full p-2 border border-gray-300 rounded mt-1"
          >
            <option value="">Select a book</option>
            {books.map((book) => (
              <option key={book._id} value={book._id}>
                {book.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-gray-700 font-medium">
            Upload PDF (Multiple Pages):
          </label>
          <input
            type="file"
            accept="application/pdf"
            onChange={handlePdfChange}
            className="w-full p-2 border border-gray-300 rounded mt-1"
          />
        </div>

        <div>
          <label className="block text-gray-700 font-medium">
            Upload Single Page:
          </label>
          <input
            type="file"
            accept="application/pdf"
            onChange={handleSinglePageChange}
            className="w-full p-2 border border-gray-300 rounded mt-1"
          />
        </div>

        <div className="mt-6 flex justify-center items-center gap-4">
          <button
            type="submit"
            className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
            disabled={loading}
          >
            {loading ? 'Creating...' : 'Add Pages'}
          </button>

          <button
            type="button"
            onClick={handleAddWhitePage}
            className="bg-gray-900 text-white px-4 py-2 rounded hover:bg-gray-800 disabled:opacity-60 disabled:cursor-not-allowed"
            disabled={!selectedBookId || whitePageLoading}
          >
            {whitePageLoading ? 'Adding...' : 'Add white page'}
          </button>

          <Link
            to="/books"
            className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600"
          >
            View Books
          </Link>
        </div>
        {selectedBookId && (
          <p className="text-center text-sm text-gray-500">
            Current pages: {pageCount}
          </p>
        )}
      </form>
    </div>
  );
}

export default CreatePagesFromBook;
